import React, { useState } from 'react';
import { IdCardIcon, PhoneIcon, CreditCardIcon, LockKeyholeIcon, EyeIcon, EyeOffIcon, ShieldCheckIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { ScreenHeader } from './ScreenHeader';
import { FormField, FieldInput } from './FormField';
import { SuccessModal } from './SuccessModal';
type Step = 'identify' | 'reset';
export function ForgotPasswordScreen({
  onBack,
  onComplete
}: {
  onBack: () => void;
  onComplete: () => void;
}) {
  const [step, setStep] = useState<Step>('identify');
  const [contact, setContact] = useState('');
  const [nic, setNic] = useState('');
  const [account, setAccount] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [success, setSuccess] = useState(false);
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F8F9FA'
  }} data-id="element-335">
      <StatusBar data-id="element-336" />
      <ScreenHeader title={step === 'identify' ? 'Reset Password' : 'Create New Password'} subtitle={step === 'identify' ? 'Verify your identity to receive a reset link.' : 'Choose a strong password you will remember.'} onBack={() => step === 'identify' ? onBack() : setStep('identify')} data-id="element-337" />
      <div className="px-5 pb-4 flex items-center gap-2" data-id="element-338">
        <StepDot active={true} label="1" data-id="element-339" />
        <div className="flex-1 h-[3px] rounded-full" style={{
        backgroundColor: step === 'reset' ? '#009AC7' : '#E5E5E5'
      }} data-id="element-340" />
        <StepDot active={step === 'reset'} label="2" data-id="element-341" />
      </div>
      <div className="flex-1 px-5 pb-4 overflow-y-auto" data-id="element-342">
        {step === 'identify' ? <form className="flex flex-col gap-4" onSubmit={e => {
        e.preventDefault();
        setStep('reset');
      }} data-id="element-343">
            <FormField label="NIC Number" icon={<IdCardIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-345" />} data-id="element-344">
              <FieldInput value={nic} onChange={e => setNic(e.target.value)} placeholder="e.g. 198512345678" autoComplete="off" data-id="element-346" />
            </FormField>
            <FormField label="Registered Mobile or Email" icon={<PhoneIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-348" />} hint="We'll send a reset code to this contact." data-id="element-347">
              <FieldInput value={contact} onChange={e => setContact(e.target.value)} placeholder="+94 7X XXX XXXX or name@email.com" inputMode="text" data-id="element-349" />
            </FormField>
            <FormField label="Bank Account Number" icon={<CreditCardIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-351" />} data-id="element-350">
              <FieldInput value={account} onChange={e => setAccount(e.target.value)} placeholder="e.g. 0123 4567 8910" inputMode="numeric" data-id="element-352" />
            </FormField>
            <div className="mt-2 flex items-start gap-2 p-3 rounded-xl" style={{
          backgroundColor: '#E6F7FB'
        }} data-id="element-353">
              <ShieldCheckIcon size={18} style={{
            color: '#009AC7'
          }} className="mt-0.5 shrink-0" data-id="element-354" />
              <p style={{
            fontSize: 12,
            color: '#1A1A1A',
            lineHeight: 1.6
          }} data-id="element-355">
                For your safety, all three details must match RDB records before
                we send a reset code.
              </p>
            </div>
            <PrimaryButton type="submit" data-id="element-356">Send Reset Code</PrimaryButton>
          </form> : <form className="flex flex-col gap-4" onSubmit={e => {
        e.preventDefault();
        setSuccess(true);
      }} data-id="element-357">
            <FormField label="New Password" icon={<LockKeyholeIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-359" />} right={<EyeToggle visible={show1} onToggle={() => setShow1(s => !s)} data-id="element-360" />} hint="Use 8+ characters with letters and numbers." data-id="element-358">
              <FieldInput type={show1 ? 'text' : 'password'} value={pw1} onChange={e => setPw1(e.target.value)} placeholder="Enter new password" data-id="element-361" />
            </FormField>
            <FormField label="Repeat New Password" icon={<LockKeyholeIcon size={20} style={{
          color: '#009AC7'
        }} data-id="element-363" />} right={<EyeToggle visible={show2} onToggle={() => setShow2(s => !s)} data-id="element-364" />} data-id="element-362">
              <FieldInput type={show2 ? 'text' : 'password'} value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Repeat new password" data-id="element-365" />
            </FormField>
            <PrimaryButton type="submit" data-id="element-366">Update Password</PrimaryButton>
          </form>}
      </div>
      <div className="flex justify-center pb-2 bg-white" data-id="element-367">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-368" />
      </div>
      <SuccessModal open={success} title="Password Updated" message="Your password has been changed successfully. Please sign in with your new password." ctaLabel="Go to Sign In" onConfirm={() => {
      setSuccess(false);
      onComplete();
    }} data-id="element-369" />
    </div>;
}
function StepDot({
  active,
  label
}: {
  active: boolean;
  label: string;
}) {
  return <div className="flex items-center justify-center rounded-full font-bold transition-colors" style={{
    width: 28,
    height: 28,
    backgroundColor: active ? '#009AC7' : '#FFFFFF',
    color: active ? '#FFFFFF' : '#7A7A7A',
    border: active ? '1.5px solid #009AC7' : '1.5px solid #C0C0C0',
    fontSize: 13
  }} data-id="element-370">
      {label}
    </div>;
}
function EyeToggle({
  visible,
  onToggle
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return <button type="button" onClick={onToggle} aria-label={visible ? 'Hide password' : 'Show password'} className="flex items-center justify-center rounded-lg transition-colors active:bg-neutral-100 -mr-1" style={{
    width: 44,
    height: 44
  }} data-id="element-371">
      {visible ? <EyeOffIcon size={22} style={{
      color: '#525252'
    }} data-id="element-372" /> : <EyeIcon size={22} style={{
      color: '#525252'
    }} data-id="element-373" />}
    </button>;
}
function PrimaryButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...rest} className="mt-2 w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99]" style={{
    backgroundColor: '#009AC7',
    minHeight: 58,
    fontSize: 16,
    boxShadow: '0 10px 24px -10px rgba(0, 154, 199, 0.55)',
    letterSpacing: '0.01em'
  }} data-id="element-374">
      {children}
    </button>;
}